import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, ButtonGroup, Alert } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { metricsService, datasetService } from '../../services/api';
import './Dashboard.css';

// Registrar os componentes necessários do Chart.js
Chart.register(...registerables);

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState('all');
  const [dateRange, setDateRange] = useState('7days');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(false);

  // Log de montagem do componente
  useEffect(() => {
    console.log('[Dashboard] Componente montado');
    return () => {
      console.log('[Dashboard] Componente desmontado');
    };
  }, []);

  useEffect(() => {
    // Carregar a lista de datasets
    const fetchDatasets = async () => {
      console.log('[Dashboard] Iniciando busca de datasets');
      try {
        setError(null);
        const response = await datasetService.getAll();
        console.log('[Dashboard] Resposta datasets:', response);
        
        // Garantir que datasets seja sempre um array
        const datasetsArray = Array.isArray(response?.data) ? response.data : [];
        console.log('[Dashboard] Datasets processados:', datasetsArray);
        
        setDatasets(datasetsArray);
      } catch (error) {
        console.error('[Dashboard] Erro ao carregar datasets:', error);
        setError('Erro ao carregar a lista de datasets. Por favor, tente novamente.');
        // Garantir que datasets seja um array vazio em caso de erro
        setDatasets([]);
      }
    };

    fetchDatasets();
  }, []);

  useEffect(() => {
    // Carregar métricas com base nos filtros
    const fetchMetrics = async () => {
      console.log('[Dashboard] Verificando autenticação');
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('[Dashboard] Token não encontrado, redirecionando para login');
        window.location.href = '/login';
        return;
      }

      console.log('[Dashboard] Iniciando busca de métricas com filtros:', {
        customDateRange,
        dateRange,
        selectedDataset,
        startDate,
        endDate
      });

      setLoading(true);
      setError(null);
      
      try {
        let filters = {};
        
        if (customDateRange && startDate && endDate) {
          filters = {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            datasetId: selectedDataset !== 'all' ? selectedDataset : undefined
          };
        } else {
          filters = {
            dateRange,
            datasetId: selectedDataset !== 'all' ? selectedDataset : undefined
          };
        }

        console.log('[Dashboard] Filtros processados:', filters);
        
        const response = await metricsService.getDailyMetrics(filters);
        console.log('[Dashboard] Métricas recebidas:', response);
        console.log('[Dashboard] Estrutura dos dados:', {
          dados: response.data,
          datas: response.data?.data?.dates,
          totais: response.data?.data?.totals,
          variacoes: response.data?.data?.variations,
          totaisAnteriores: response.data?.data?.previousTotals
        });
        
        if (response?.data?.data) {
          setMetrics(response.data.data);
        } else {
          console.error('[Dashboard] Dados inválidos recebidos');
          throw new Error('Dados inválidos recebidos do servidor');
        }
      } catch (error) {
        console.error('[Dashboard] Erro ao carregar métricas:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setError('Erro ao carregar métricas. Por favor, tente novamente.');
        setMetrics(null);
      } finally {
        setLoading(false);
        console.log('[Dashboard] Estado final após busca:', {
          loading: false,
          hasError: !!error,
          hasMetrics: !!metrics,
          datasetsLength: datasets.length
        });
      }
    };

    fetchMetrics();
  }, [selectedDataset, dateRange, customDateRange, startDate, endDate]);

  // Log de alterações nos filtros
  useEffect(() => {
    console.log('[Dashboard] Filtros atualizados:', {
      selectedDataset,
      dateRange,
      customDateRange,
      startDate,
      endDate
    });
  }, [selectedDataset, dateRange, customDateRange, startDate, endDate]);

  // Função para selecionar "Hoje"
  const handleSelectToday = () => {
    setCustomDateRange(true);
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
  };

  // Função para selecionar "Ontem"
  const handleSelectYesterday = () => {
    setCustomDateRange(true);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    setStartDate(yesterday);
    setEndDate(yesterday);
  };

  // Função para selecionar período predefinido
  const handleSelectPredefinedRange = (range) => {
    setCustomDateRange(false);
    setDateRange(range);
  };

  // Função para aplicar o filtro de datas personalizado
  const handleApplyCustomDateRange = () => {
    if (startDate && endDate) {
      setCustomDateRange(true);
    }
  };

  // Preparar dados para o gráfico
  const chartData = {
    labels: metrics?.dates || [],
    datasets: [
      {
        label: 'Visualizações de Página',
        data: metrics?.viewPage || [],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4
      },
      {
        label: 'Interações com Bot',
        data: metrics?.interactedBot || [],
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        tension: 0.4
      },
      {
        label: 'Entradas no Canal',
        data: metrics?.enteredChannel || [],
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Métricas Diárias'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  // Função para formatar a data para exibição
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('pt-BR');
  };

  // Determinar o título do período selecionado
  const getPeriodTitle = () => {
    if (customDateRange) {
      if (startDate && endDate) {
        if (startDate.getTime() === endDate.getTime()) {
          return `Dados de ${formatDate(startDate)}`;
        }
        return `Dados de ${formatDate(startDate)} até ${formatDate(endDate)}`;
      }
      return 'Período Personalizado';
    }
    
    switch (dateRange) {
      case 'today':
        return 'Dados de Hoje';
      case 'yesterday':
        return 'Dados de Ontem';
      case '7days':
        return 'Últimos 7 dias';
      case '30days':
        return 'Últimos 30 dias';
      case '90days':
        return 'Últimos 90 dias';
      default:
        return 'Métricas Diárias';
    }
  };

  // Função para calcular taxa de conversão
  const calculateConversionRate = (numerator, denominator) => {
    if (!denominator) return 0;
    return ((numerator / denominator) * 100).toFixed(2);
  };

  // Função para formatar números
  const formatNumber = (number) => {
    return new Intl.NumberFormat('pt-BR').format(number || 0);
  };

  return (
    <Container fluid className="dashboard-container">
      <h1 className="mb-4">Dashboard</h1>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Dataset</Form.Label>
            <Form.Select 
              value={selectedDataset} 
              onChange={(e) => setSelectedDataset(e.target.value)}
            >
              <option value="all">Todos os Datasets</option>
              {Array.isArray(datasets) && datasets.map(dataset => (
                <option key={dataset._id} value={dataset._id}>
                  {dataset.nome}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Label>Período</Form.Label>
          <div className="d-flex flex-column">
            <ButtonGroup className="mb-2">
              <Button 
                variant={customDateRange && startDate && endDate && startDate.getTime() === endDate.getTime() && new Date().getDate() === startDate.getDate() ? "primary" : "outline-primary"}
                onClick={handleSelectToday}
              >
                Hoje
              </Button>
              <Button 
                variant={customDateRange && startDate && endDate && startDate.getTime() === endDate.getTime() && new Date().getDate() - 1 === startDate.getDate() ? "primary" : "outline-primary"}
                onClick={handleSelectYesterday}
              >
                Ontem
              </Button>
              <Button 
                variant={!customDateRange && dateRange === '7days' ? "primary" : "outline-primary"}
                onClick={() => handleSelectPredefinedRange('7days')}
              >
                7 dias
              </Button>
              <Button 
                variant={!customDateRange && dateRange === '30days' ? "primary" : "outline-primary"}
                onClick={() => handleSelectPredefinedRange('30days')}
              >
                30 dias
              </Button>
              <Button 
                variant={!customDateRange && dateRange === '90days' ? "primary" : "outline-primary"}
                onClick={() => handleSelectPredefinedRange('90days')}
              >
                90 dias
              </Button>
            </ButtonGroup>
            
            <div className="custom-date-range">
              <div className="d-flex align-items-center">
                <div className="date-picker-container me-2">
                  <Form.Label>De</Form.Label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="Data inicial"
                  />
                </div>
                <div className="date-picker-container me-2">
                  <Form.Label>Até</Form.Label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="Data final"
                  />
                </div>
                <div className="d-flex align-items-end">
                  <Button 
                    variant="primary" 
                    onClick={handleApplyCustomDateRange}
                    disabled={!startDate || !endDate}
                    className="mb-1"
                  >
                    Aplicar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <h4 className="text-center mb-4">{getPeriodTitle()}</h4>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="metric-card h-100">
            <Card.Body>
              <Card.Title>Visualizações de Página</Card.Title>
              <h2>{formatNumber(metrics?.totals?.viewPage || 0)}</h2>
              <div className="metric-details">
                <small className="text-muted">Total de visualizações no período</small>
                {metrics?.variations?.viewPage !== undefined && (
                  <div className={`variation ${Number(metrics.variations.viewPage) >= 0 ? 'positive' : 'negative'}`}>
                    {Number(metrics.variations.viewPage) >= 0 ? '↑' : '↓'} {Math.abs(metrics.variations.viewPage)}%
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card h-100">
            <Card.Body>
              <Card.Title>Interações com Bot</Card.Title>
              <h2>{formatNumber(metrics?.totals?.interactedBot || 0)}</h2>
              <div className="metric-details">
                <small className="text-muted">Taxa de Conversão:</small>
                <div className="conversion-rate">
                  {calculateConversionRate(metrics?.totals?.interactedBot || 0, metrics?.totals?.viewPage || 0)}%
                </div>
                {metrics?.variations?.interactedBot !== undefined && (
                  <div className={`variation ${Number(metrics.variations.interactedBot) >= 0 ? 'positive' : 'negative'}`}>
                    {Number(metrics.variations.interactedBot) >= 0 ? '↑' : '↓'} {Math.abs(metrics.variations.interactedBot)}%
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card h-100">
            <Card.Body>
              <Card.Title>Entradas no Canal</Card.Title>
              <h2>{formatNumber(metrics?.totals?.enteredChannel || 0)}</h2>
              <div className="metric-details">
                <small className="text-muted">Taxa de Conversão:</small>
                <div className="conversion-rate">
                  {calculateConversionRate(metrics?.totals?.enteredChannel || 0, metrics?.totals?.interactedBot || 0)}%
                </div>
                {metrics?.variations?.enteredChannel !== undefined && (
                  <div className={`variation ${Number(metrics.variations.enteredChannel) >= 0 ? 'positive' : 'negative'}`}>
                    {Number(metrics.variations.enteredChannel) >= 0 ? '↑' : '↓'} {Math.abs(metrics.variations.enteredChannel)}%
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="metric-card h-100">
            <Card.Body>
              <Card.Title>Taxa de Conversão Total</Card.Title>
              <h2>{calculateConversionRate(metrics?.totals?.enteredChannel || 0, metrics?.totals?.viewPage || 0)}%</h2>
              <div className="metric-details">
                <small className="text-muted">Visualizações → Entradas no Canal</small>
                <div className="previous-period">
                  <small className="text-muted">Período anterior: </small>
                  {calculateConversionRate(metrics?.previousTotals?.enteredChannel || 0, metrics?.previousTotals?.viewPage || 0)}%
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      ) : (
        <>
          {metrics ? (
            <Card>
              <Card.Body>
                <h2 className="mb-4">{getPeriodTitle()}</h2>
                <div className="chart-container">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Alert variant="info">
              Nenhum dado disponível para o período selecionado.
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};

export default Dashboard; 